import {
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
} from 'lucide-react'
import { useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useClearPlanFilters } from 'components/hooks/member_onboarding_v2/useClearPlanFilters'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import {
  Banner,
  Button,
  ButtonGroup,
  TextButton,
  Tooltip,
  useBreakpoint,
} from 'slab'
import { formatMoney } from 'utils'
import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansDetails as useGetMedicalPlansDetails,
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment,
} from 'utils/web_api'

import { BackButton } from '../components/BackButton'
import { MedicalPlanDetailHeader } from '../components/MedicalPlanDetailHeader'
import { MedicalPlanDetailSummary } from '../components/MedicalPlanDetailSummary'
import { getRemainingAllowance } from '../components/MemberOnboardingPlanCard'
import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { PageErrorBanner } from '../components/PageErrorBanner'
import { TIERED_ALLOWANCE_TOOLTIP_TEXT } from '../components/PlanCard/Premium'
import { MedicalDetailPlanTable } from '../components/PlanTable/DetailPlanTable'
import { type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import {
  calculateRemainingBudgetForTieredAllowance,
  calculateTieredAllowanceBreakdown,
} from '../utils'

export function PlanCost ({
  planDisplayItem,
  onSelectPlan,
  disableSelectPlan,
  submitError,
}: {
  planDisplayItem: MedicalPlanDisplayItem;
  onSelectPlan: () => void;
  disableSelectPlan: boolean;
  submitError: GrapeAPIErrorType | null;
}) {
  const { onboardingData } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()
  const isMobile = !breakpointIsAtLeast('xs')

  const [isCollapsed, setIsCollapsed] = useState(() => isMobile)

  const premium = Number(
    ((planDisplayItem.total_premium.subunits || 0) / 100).toFixed(2)
  )

  const isMemberOnTieredAllowance =
    !!planDisplayItem.tiered_allowance_employee_only_premium
  let difference = 0
  let employerPays = ''
  if (isMemberOnTieredAllowance) {
    difference = calculateRemainingBudgetForTieredAllowance({
      planDisplayItem,
    })
    const { formattedEmployerContribution } = calculateTieredAllowanceBreakdown(
      {
        allowanceAmount: onboardingData?.allowance?.allowance_amount,
        medicalPlanDisplayItem: planDisplayItem,
      }
    )
    employerPays = formattedEmployerContribution
  } else if (onboardingData) {
    difference = getRemainingAllowance(onboardingData, premium) ?? 0
  }

  const formattedPremium = planDisplayItem.total_premium.formatted_amount
  let textColor = 'text-gray-800'
  if (!isMemberOnTieredAllowance) {
    if (difference < 0) {
      textColor = 'text-red-500'
    } else if (difference > 0) {
      textColor = 'text-green-500'
    }
  }

  const currentPlanEnrollment =
    onboardingData?.enrollments?.find((e) => e.covers_medical) || null

  const isSelected =
    currentPlanEnrollment &&
    currentPlanEnrollment.carrier_name?.toLowerCase() ===
      planDisplayItem.plan.carrier_name.toLowerCase() &&
    currentPlanEnrollment.plan_code?.toLowerCase() === planDisplayItem.plan.id

  return (
    <div className='shrink-0 grow xs:max-w-[17.5rem] p-4 border-t -mx-4 xs:mx-0 xs:border bg-white border-gray-200 xs:rounded-md shadow-[0_-0.5rem_0.75rem] shadow-black/10 xs:shadow-lg h-fit sticky bottom-0 xs:top-12 mt-8 xs:mt-0'>
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-gray-500 body-sm mb-1'>Plan cost</h2>
          <div className='heading-xl text-gray-800 mb-4'>
            {formattedPremium}
            <span className='text-gray-500 heading-sm font-normal'>/mo</span>
          </div>
        </div>
        {isMobile && (
          <div className='body-sm self-start'>
            <TextButton
              color='gray'
              onClick={() => setIsCollapsed((prev) => !prev)}
              rightIcon={isCollapsed ? ChevronDownIcon : ChevronUpIcon}
            >
              {isCollapsed ? 'Show budget' : 'Hide budget'}
            </TextButton>
          </div>
        )}
      </div>
      {!isCollapsed && !isMemberOnTieredAllowance && (
        <dl className='grid grid-cols-2 gap-x-4 gap-y-3 mb-8 bg-gray-50 p-3 rounded-md'>
          <dt className='text-gray-800 body-sm text-nowrap'>Monthly budget</dt>
          <dd className='text-gray-800 body-sm text-right'>
            {onboardingData?.allowance?.allowance_amount?.formatted_amount}
          </dd>
          <dt className='text-gray-800 body-sm text-nowrap'>Plan cost</dt>
          <dd className='text-gray-800 body-sm text-right'>
            -{formattedPremium}
          </dd>
          <dt className='text-gray-800 body-bold text-nowrap'>
            {difference < 0 ? "You'll owe" : 'Remaining'}
          </dt>
          <dd className={`${textColor} body-bold text-right`}>
            {formatMoney(Math.abs(difference))}
          </dd>
        </dl>
      )}
      {!isCollapsed && isMemberOnTieredAllowance && (
        <dl className='grid grid-cols-[1fr_auto] gap-x-4 gap-y-3 mb-8 bg-gray-50 p-3 rounded-md'>
          <dt className='text-gray-800 body-sm text-nowrap'>
            Your employer pays
          </dt>
          <dd className='text-gray-800 body-sm text-right'>{employerPays}</dd>
          <dt className='text-gray-800 body-bold text-nowrap flex items-center gap-x-1'>
            You pay
            <Tooltip content={TIERED_ALLOWANCE_TOOLTIP_TEXT} side='bottom'>
              <InfoIcon
                aria-label='Info icon'
                className='text-gray-400'
                size={12}
              />
            </Tooltip>
          </dt>
          <dd className={`${textColor} body-bold text-right`}>
            {formatMoney(difference >= 0 ? 0 : -difference)}
          </dd>
        </dl>
      )}
      {!isSelected && (
        <>
          <ButtonGroup align='center' layout='vertical'>
            <Button
              size='large'
              variant='primary'
              onClick={onSelectPlan}
              disabled={disableSelectPlan}
            >
              Select plan
            </Button>
          </ButtonGroup>
          <p className='text-gray-500 caption text-center mt-2'>
            You won't be enrolled yet
          </p>
        </>
      )}
      {/* TODO: change the below error message into toast */}
      {submitError && (
        <div className='mt-2'>
          <Banner
            title='A problem has occurred while processing your request'
            type='critical'
          >
            <OnboardingGenericErrorMessage error={submitError} />
          </Banner>
        </div>
      )}
    </div>
  )
}

function BaseMedicalPlanDetailPage ({
  navigateToNextStep,
  navigateToPreviousStep,
  navigateToSpecificStep,
  metadata,
  planYear,
  readOnly,
}: StepComponentProps & {
  planYear?: number;
  readOnly?: boolean;
}) {
  const { onboardingData, enrollmentEventId } = useOnboarding()
  const [submitError, setSubmitError] = useState<GrapeAPIErrorType | null>(
    null
  )
  const createEnrollment = useCreateEnrollment({
    mutation: {
      onSuccess: () => {
        navigateToNextStep?.()
      },
      onError: (error) => {
        setSubmitError(error as GrapeAPIErrorType)
      },
    },
  })

  useClearPlanFilters()

  const planId = metadata?.planIds[0]

  const {
    data: plans,
    isLoading: isLoadingPlan,
    error: medicalPlanError,
  } = useGetMedicalPlansDetails(
    enrollmentEventId,
    {
      'plan_ids[]': [planId],
      plan_year: planYear,
    },
    {
      query: {
        retry: false,
        queryKey: [
          'enrollmentEvent',
          enrollmentEventId,
          'medical_plans',
          planId,
        ].concat(planYear ? ['plan_year', planYear] : []),
        enabled: !readOnly || (readOnly && !!planYear),
      },
    }
  )

  function renderContent () {
    if (medicalPlanError) {
      return <PageErrorBanner error={medicalPlanError as GrapeAPIErrorType} />
    }

    const planDisplayItem = plans?.data?.[0]
    const plan = planDisplayItem?.plan

    if ((isLoadingPlan && !plans) || (readOnly && !planYear)) {
      return (
        <div
          data-testid='LoadingSpinnerCentered'
          className='flex items-center justify-center h-[calc(100vh-400px)]'
        >
          <LoadingSpinnerCentered />
        </div>
      )
    }

    if (!isLoadingPlan && (!plans || !planDisplayItem || !plan)) {
      return (
        <Banner title='Plan not found' type='critical'>
          <OnboardingGenericErrorMessage />
        </Banner>
      )
    }

    // TypeScript needs help understanding these are defined
    const safePlan = plan as NonNullable<typeof plan>
    const safePlanDisplayItem = planDisplayItem as NonNullable<
      typeof planDisplayItem
    >

    const handleSelectPlan = () => {
      if (readOnly) return

      createEnrollment.mutate({
        enrollmentEventId,
        data: {
          covers_medical: true,
          covers_dental: false,
          covers_vision: false,
          plan_code: safePlan.id,
          carrier_name: safePlan.carrier_name,
          quoted_premium:
            (safePlanDisplayItem.total_premium?.subunits || 0) / 100,
          employee_only_premium:
            (safePlanDisplayItem.tiered_allowance_employee_only_premium
              ?.subunits || 0) / 100,
        },
      })
    }

    const isIndividual =
      (onboardingData?.additional_participants || []).length === 0

    const providerInNetworkMap = safePlanDisplayItem.provider_in_network_map
    const inNetworkProvidersCount =
      safePlanDisplayItem.in_network_providers_count

    return (
      <div className='flex flex-col xs:flex-row justify-between'>
        <div className='xs:basis-[36rem] max-w-[48rem] shrink-0 grow xs:pr-10 xl:pr-16'>
          <MedicalPlanDetailHeader plan={safePlan} planYear={planYear} />
          <MedicalPlanDetailSummary
            plan={safePlan}
            isIndividual={isIndividual}
            providerInNetworkMap={providerInNetworkMap}
            inNetworkProvidersCount={inNetworkProvidersCount}
            onSelectMorePlansForVerification={() => navigateToSpecificStep?.('in_network_verification')}
            openInNetworkVerification={metadata?.openInNetworkVerification}
          />
          <div className='-mx-4 xs:mx-0'>
            <MedicalDetailPlanTable planDisplayItems={[safePlanDisplayItem]} navigateToSpecificStep={navigateToSpecificStep} />
          </div>
        </div>
        {!readOnly && (
          <PlanCost
            planDisplayItem={safePlanDisplayItem}
            disableSelectPlan={createEnrollment.isPending || !plan}
            onSelectPlan={handleSelectPlan}
            submitError={submitError}
          />
        )}
      </div>
    )
  }

  return (
    <div data-testid='MedicalPlanDetailPage' className='xs:-mt-[4.5rem]'>
      <div className='-ml-4 mb-4 xs:mb-8'>
        <BackButton onClick={() => navigateToPreviousStep?.()} />
      </div>
      {renderContent()}
    </div>
  )
}

export function MedicalPlanDetailPage (props: StepComponentProps) {
  return (
    <BaseMedicalPlanDetailPage {...props} />
  )
}

export function RenewalCurrMedicalPlanDetailPage (props: StepComponentProps) {
  const { planChangeSummary } = useOnboarding()
  return (
    <div className='flex justify-center py-10'>
      <BaseMedicalPlanDetailPage
        {...props}
        planYear={planChangeSummary?.current_plan_year.plan_year}
        readOnly
      />
    </div>
  )
}

export function RenewalNextMedicalPlanDetailPage (props: StepComponentProps) {
  const { planChangeSummary } = useOnboarding()

  return (
    <div className='flex justify-center py-10'>
      <BaseMedicalPlanDetailPage
        {...props}
        planYear={planChangeSummary?.next_plan_year.plan_year}
        readOnly
      />
    </div>
  )
}
